import axios, { type AxiosResponse } from 'axios';
import { logger } from '@trading-utils/logger';

import { processBackendError } from '@/utils/error.utils';

import i18n from '@/i18n/config';

import { apiRoutes } from '@/config/apiRoutes';

import type { Action, Company } from '@/interfaces/app.interfaces';

export const getAllPendingActions = async (): Promise<Action[]> => {
  const url = `${apiRoutes.base}${apiRoutes.moeveActions.base}`;

  try {
    const response: AxiosResponse<Action[]> = await axios.get(url);

    logger.info(`Fetched ${response.data.length} pending actions`);

    return response.data;
  } catch (error) {
    logger.error('Error fetching pending actions', { url, error });
    throw processBackendError(error, i18n.t('errors.moeveActions.errorGetPendingActions'));
  }
};

export const changeActionStatus = async ({
  actionId,
  statusId,
  role,
  companyIds,
}: {
  actionId: number;
  statusId: number;
  role?: string;
  companyIds?: number[];
}): Promise<Action> => {
  const url = `${apiRoutes.base}${apiRoutes.moeveActions.base}${apiRoutes.moeveActions.changeStatus}/${actionId}`;

  try {
    const response: AxiosResponse<Action> = await axios.patch(url, { statusId, role, companyIds });

    logger.info(`Changed action status to ${statusId}`);

    return response.data;
  } catch (error) {
    logger.error('Error changing action status', { url, error });
    throw processBackendError(error, i18n.t('errors.moeveActions.errorChangeActionStatus'));
  }
};

export const getAllActiveCompanies = async (): Promise<Company[]> => {
  const url = `${apiRoutes.base}${apiRoutes.company.base}`;

  try {
    const response: AxiosResponse<Company[]> = await axios.get(url);

    logger.info(`Fetched ${response.data.length} active companies`);

    return response.data;
  } catch (error) {
    logger.error('Error fetching active companies', { url, error });
    throw processBackendError(error, i18n.t('errors.moeveActions.errorGetActiveCompanies'));
  }
};

export const changeCompanyType = async (companyId: number, companyTypeId: number): Promise<Company> => {
  const url = `${apiRoutes.base}${apiRoutes.company.base}/${companyId}`;

  try {
    const response: AxiosResponse<Company> = await axios.patch(url, { companyTypeId });

    logger.info(`Changed company type to ${companyTypeId}`);

    return response.data;
  } catch (error) {
    logger.error('Error changing company type', { url, error });
    throw processBackendError(error, i18n.t('errors.moeveActions.errorChangeCompanyType'));
  }
};

export const deleteCompany = async (companyId: number): Promise<Company> => {
  const url = `${apiRoutes.base}${apiRoutes.company.base}/${companyId}`;

  try {
    const response: AxiosResponse<Company> = await axios.delete(url);

    logger.info(`Deleted company ${companyId}`);

    return response.data;
  } catch (error) {
    logger.error('Error deleting company', { url, error });
    throw processBackendError(error, i18n.t('errors.moeveActions.errorDeleteCompany'));
  }
};
